/**
 * Menu de anexar do composer — lista estilo WhatsApp no canto, ícones sem círculo.
 */
import React, { type ChangeEvent, type RefObject } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { AppIcon, type IconName } from '@/components/icons/AppIcon';
import { PixIcon } from '@/components/icons/CustomIcons';
import { cn } from '@/lib/utils';
import { useChatContext } from '../../../../contexts/useChatContext';
import { isOfficialWhatsappConnection } from '../../hooks/useChatConnection';
import {
    WHATSAPP_CHAT_DOCUMENT_ACCEPT,
    WHATSAPP_CHAT_MEDIA_ACCEPT,
} from '../../hooks/whatsappMediaLimits';

interface ChatAttachMenuProps {
    chatPlatform?: 'whatsapp' | 'instagram';
    mediaInputRef: RefObject<HTMLInputElement>;
    documentInputRef: RefObject<HTMLInputElement>;
    onMediaChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onDocumentChange: (e: ChangeEvent<HTMLInputElement>) => void;
    onOpenContact: () => void;
    onOpenPix: () => void;
    onOpenLocation: () => void;
}

type AttachMenuItem = {
    label: string;
    icon: IconName;
    iconClass: string;
    onSelect: () => void;
};

export const ChatAttachMenu: React.FC<ChatAttachMenuProps> = ({
    chatPlatform = 'whatsapp',
    mediaInputRef,
    documentInputRef,
    onMediaChange,
    onDocumentChange,
    onOpenContact,
    onOpenPix,
    onOpenLocation,
}) => {
    const { connections, selectedConnection, activeConversation } = useChatContext();
    const isInstagramDirect = chatPlatform === 'instagram';
    const officialApi = !isInstagramDirect && isOfficialWhatsappConnection(
        connections,
        selectedConnection,
        activeConversation?.connectionId,
    );

    const mediaItem: AttachMenuItem = {
        label: 'Fotos e vídeos',
        icon: 'image',
        iconClass: 'text-primary',
        onSelect: () => mediaInputRef.current?.click(),
    };

    const locationItem: AttachMenuItem = {
        label: 'Localização',
        icon: 'location',
        iconClass: 'text-warning',
        onSelect: onOpenLocation,
    };

    const items: AttachMenuItem[] = isInstagramDirect
        ? [mediaItem, locationItem]
        : officialApi
        ? [
              {
                  label: 'Documento',
                  icon: 'file-text',
                  iconClass: 'text-destructive',
                  onSelect: () => documentInputRef.current?.click(),
              },
              mediaItem,
          ]
        : [
              {
                  label: 'Documento',
                  icon: 'file-text',
                  iconClass: 'text-destructive',
                  onSelect: () => documentInputRef.current?.click(),
              },
              mediaItem,
              {
                  label: 'Contato',
                  icon: 'user',
                  iconClass: 'text-info',
                  onSelect: onOpenContact,
              },
              {
                  label: 'PIX',
                  icon: 'pix',
                  iconClass: '',
                  onSelect: onOpenPix,
              },
              locationItem,
          ];

    return (
        <>
            <input
                ref={mediaInputRef}
                type="file"
                id="chat-media-attachment-input"
                name="chat-media-attachment-input"
                multiple
                accept={WHATSAPP_CHAT_MEDIA_ACCEPT}
                className="hidden"
                onChange={onMediaChange}
            />
            {!isInstagramDirect && (
                <input
                    ref={documentInputRef}
                    type="file"
                    id="chat-document-attachment-input"
                    name="chat-document-attachment-input"
                    multiple
                    accept={WHATSAPP_CHAT_DOCUMENT_ACCEPT}
                    className="hidden"
                    onChange={onDocumentChange}
                />
            )}

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button
                        type="button"
                        className="chat-composer__icon"
                        title="Anexar"
                        aria-label="Anexar"
                    >
                        <AppIcon name="plus" className="size-[18px]" aria-hidden />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    className={cn(
                        isInstagramDirect ? 'h-[96px]' : 'h-[238px]',
                        'w-[122px] overflow-hidden rounded-xl border-subtle bg-card p-1',
                        'text-default shadow-md'
                    )}
                    align="start"
                    side="top"
                    sideOffset={6}
                    alignOffset={-8}
                    collisionPadding={8}
                >
                    <nav aria-label="Anexar à conversa" className="flex h-full flex-col justify-between py-0.5">
                        {items.map((item) => (
                            <DropdownMenuItem
                                key={item.label}
                                onSelect={item.onSelect}
                                className={cn(
                                    'flex min-h-0 flex-1 cursor-pointer items-center gap-2 rounded-lg px-2',
                                    'focus:bg-surface-subtle data-highlighted:bg-surface-subtle'
                                )}
                            >
                                {item.icon === 'pix' ? (
                                    <PixIcon className="size-4" aria-hidden />
                                ) : (
                                    <AppIcon
                                        name={item.icon}
                                        className={cn('size-4 shrink-0', item.iconClass)}
                                        aria-hidden
                                    />
                                )}
                                <span className="min-w-0 flex-1 truncate text-left text-caption font-light leading-tight text-secondary">
                                    {item.label}
                                </span>
                            </DropdownMenuItem>
                        ))}
                    </nav>
                </DropdownMenuContent>
            </DropdownMenu>
        </>
    );
};
